/**
 * PersonDialog — category dispatcher for the People form.
 *
 * Per docs/port-design/people-form-port.md §9: one dispatcher + shared shell +
 * per-category identity/derivation components. Looks at `person.customerType`,
 * resolves the entity group via `findGroupForType`, and routes to the right
 * per-category dialog.
 *
 * v0.1.43: all five non-Personal categories now have real dialogs — Business,
 * Trust, Estate, Fiduciary, Government. CategoryStub retired from the switch;
 * kept in the tree only as a fallback for unknown group tags.
 */

import type { RaoPerson } from '../../types';
import { findGroupForType } from './findGroupForType';
import { IndividualPersonDialog } from './individual/IndividualPersonDialog';
import { SolePropDialog } from './soleProp';
import { BusinessPersonDialog } from './business/BusinessPersonDialog';
import { TrustPersonDialog } from './trust/TrustPersonDialog';
import { EstatePersonDialog } from './estate/EstatePersonDialog';
import { FiduciaryPersonDialog } from './fiduciary/FiduciaryPersonDialog';
import { GovernmentPersonDialog } from './government/GovernmentPersonDialog';
import { SpecialPurposePersonDialog } from './specialPurpose/SpecialPurposePersonDialog';
import { CategoryStub } from './CategoryStub';

export type PersonDialogMode = 'review-add' | 'new' | 'edit';

export interface PersonDialogProps {
  open: boolean;
  mode: PersonDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  /** Required for DocumentSlotsSection inside child dialogs. */
  cartId: string;
  /** Officer code for document upload metadata. Optional — falls back to 'UNKNOWN' if unavailable. */
  officerCode?: string;
  /** The early Core search (PeopleStep). Business / trust dialogs use it for the "No EIN / TIN"
   *  proof: a search by the entity name (Bill 2026-10-14). */
  onEarlyDupCheck?: (draft: RaoPerson, markNoTinCleared?: (key: string) => void) => void;
}

export function PersonDialog(props: PersonDialogProps): JSX.Element | null {
  const group = findGroupForType(props.person.customerType);

  switch (group) {
    case 'personal':
      return <IndividualPersonDialog {...props} />;

    case 'soleProp':
      return <SolePropDialog {...props} />;

    case 'business':
      return <BusinessPersonDialog {...props} />;

    case 'trust':
      return <TrustPersonDialog {...props} />;

    case 'estate':
      return <EstatePersonDialog {...props} />;

    case 'fiduciary':
      return <FiduciaryPersonDialog {...props} />;

    case 'government':
      return <GovernmentPersonDialog {...props} />;

    case 'specialPurpose':
      return <SpecialPurposePersonDialog {...props} />;

    default:
      // Unresolved customerType — fall back to Individual (most permissive).
      // Happens for brand-new persons before customerType is set AND for any
      // future entity group tag we haven't written a dialog for yet.
      // CategoryStub stays importable for ad-hoc use but is no longer on the
      // hot path since every known group now has a real dialog.
      void CategoryStub;
      return <IndividualPersonDialog {...props} />;
  }
}
